import { type MessageId } from "@cmmd-forge/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { type ChatMessage, type Thread } from "../../types";
import {
  deriveAttachmentPreviewPromotionCandidates,
  removeAttachmentPreviewHandoff,
  revokeBlobPreviewUrl,
  revokeUserMessagePreviewUrls,
  upsertAttachmentPreviewHandoff,
} from "../ChatView.logic";

export function useAttachmentPreviewHandoffs(input: {
  readonly serverMessages: Thread["messages"] | undefined;
  readonly optimisticUserMessagesRef: { current: ChatMessage[] };
}): {
  readonly attachmentPreviewHandoffByMessageId: Record<string, string[]>;
  readonly clearAttachmentPreviewHandoffs: () => void;
  readonly handoffAttachmentPreviews: (messageId: MessageId, previewUrls: string[]) => void;
} {
  const { optimisticUserMessagesRef } = input;
  const [attachmentPreviewHandoffByMessageId, setAttachmentPreviewHandoffByMessageId] = useState<
    Record<string, string[]>
  >({});
  const attachmentPreviewHandoffByMessageIdRef = useRef<Record<string, string[]>>({});
  const attachmentPreviewPromotionInFlightByMessageIdRef = useRef<Record<string, true>>({});

  useEffect(() => {
    attachmentPreviewHandoffByMessageIdRef.current = attachmentPreviewHandoffByMessageId;
  }, [attachmentPreviewHandoffByMessageId]);

  const clearAttachmentPreviewHandoff = useCallback(
    (messageId: MessageId, previewUrls?: ReadonlyArray<string>) => {
      delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
      const removal = removeAttachmentPreviewHandoff({
        handoffsByMessageId: attachmentPreviewHandoffByMessageIdRef.current,
        messageId,
        previewUrls,
      });
      setAttachmentPreviewHandoffByMessageId((existing) => {
        const nextRemoval = removeAttachmentPreviewHandoff({
          handoffsByMessageId: existing,
          messageId,
          previewUrls,
        });
        if (!nextRemoval.changed) {
          return existing;
        }
        attachmentPreviewHandoffByMessageIdRef.current = nextRemoval.nextHandoffsByMessageId;
        return nextRemoval.nextHandoffsByMessageId;
      });
      for (const previewUrl of removal.previewUrlsToRevoke) {
        revokeBlobPreviewUrl(previewUrl);
      }
    },
    [],
  );

  const clearAttachmentPreviewHandoffs = useCallback(() => {
    attachmentPreviewPromotionInFlightByMessageIdRef.current = {};
    for (const previewUrls of Object.values(attachmentPreviewHandoffByMessageIdRef.current)) {
      for (const previewUrl of previewUrls) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    attachmentPreviewHandoffByMessageIdRef.current = {};
    setAttachmentPreviewHandoffByMessageId({});
  }, []);

  useEffect(() => {
    const optimisticMessages = optimisticUserMessagesRef.current;
    return () => {
      clearAttachmentPreviewHandoffs();
      for (const message of optimisticMessages) {
        revokeUserMessagePreviewUrls(message);
      }
    };
  }, [clearAttachmentPreviewHandoffs, optimisticUserMessagesRef]);

  const handoffAttachmentPreviews = useCallback((messageId: MessageId, previewUrls: string[]) => {
    const upsert = upsertAttachmentPreviewHandoff({
      handoffsByMessageId: attachmentPreviewHandoffByMessageIdRef.current,
      messageId,
      previewUrls,
    });
    if (!upsert.changed) return;

    for (const previewUrl of upsert.previewUrlsToRevoke) {
      revokeBlobPreviewUrl(previewUrl);
    }
    setAttachmentPreviewHandoffByMessageId((existing) => {
      const nextUpsert = upsertAttachmentPreviewHandoff({
        handoffsByMessageId: existing,
        messageId,
        previewUrls,
      });
      attachmentPreviewHandoffByMessageIdRef.current = nextUpsert.nextHandoffsByMessageId;
      return nextUpsert.nextHandoffsByMessageId;
    });
  }, []);

  useEffect(() => {
    if (
      typeof Image === "undefined" ||
      !input.serverMessages ||
      input.serverMessages.length === 0
    ) {
      return;
    }

    const cleanups: Array<() => void> = [];

    for (const {
      messageId,
      handoffPreviewUrls,
      serverPreviewUrls,
    } of deriveAttachmentPreviewPromotionCandidates({
      serverMessages: input.serverMessages,
      handoffsByMessageId: attachmentPreviewHandoffByMessageId,
      promotionInFlightByMessageId: attachmentPreviewPromotionInFlightByMessageIdRef.current,
    })) {
      attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId] = true;

      let cancelled = false;
      const imageInstances: HTMLImageElement[] = [];

      const preloadServerPreviews = Promise.all(
        serverPreviewUrls.map(
          (previewUrl) =>
            new Promise<void>((resolve, reject) => {
              const image = new Image();
              imageInstances.push(image);
              const handleLoad = () => resolve();
              const handleError = () =>
                reject(new Error(`Failed to load server preview for ${messageId}.`));
              image.addEventListener("load", handleLoad, { once: true });
              image.addEventListener("error", handleError, { once: true });
              image.src = previewUrl;
            }),
        ),
      );

      void preloadServerPreviews
        .then(() => {
          if (cancelled) {
            return;
          }
          clearAttachmentPreviewHandoff(messageId as MessageId, handoffPreviewUrls);
        })
        .catch(() => {
          if (!cancelled) {
            delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
          }
        });

      cleanups.push(() => {
        cancelled = true;
        delete attachmentPreviewPromotionInFlightByMessageIdRef.current[messageId];
        for (const image of imageInstances) {
          image.src = "";
        }
      });
    }

    return () => {
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  }, [attachmentPreviewHandoffByMessageId, clearAttachmentPreviewHandoff, input.serverMessages]);

  return {
    attachmentPreviewHandoffByMessageId,
    clearAttachmentPreviewHandoffs,
    handoffAttachmentPreviews,
  };
}
